import * as React from "react";

import {WithStyles, withStyles} from '@material-ui/core/styles';
import Button from "@components/common/button/Button";
import ImgAvatar from "@components/common/avatar/ImgAvatar";
import PackingListItem from './PackingListItem';
import NewPackingListItemRow from './NewPackingListItemRow';
import {findNextHandover, groupPackingItems, handoverHasBeen, IPackingGroup, packingItemsForSchedule} from './packingList';
import {IFamilyMember} from '@app-types/websocket/family.types';
import TranslateNew from "@translate/TranslateNew";
import {ISchedule} from "@app-types/websocket/schedule.types";
import {ICompleteTodo, IHideTodo, ITodo} from "@app-types/websocket/todo.types";
import {schoolAgeFromFamily} from '@helpers/schoolHelper';
import {FamilyMemberType} from '@services/family/family.constants';
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';
import {NextHandoverPerScheduleStyle as styles} from './NextHandoverPerSchedule.style';


const tn: TranslateNew = TranslateNew.getInstance();





interface IProps extends WithStyles<typeof styles> {
  myAccountId: number;
  family: IFamilyMember[];
  schedule: ISchedule;
  schedules: ISchedule[];
  todos: ITodo[];
  hide:boolean;
  single:boolean;
  completeTodo: (completed: ICompleteTodo) => void;
  seenTodo: (data: IHideTodo) => void;
  completeHandover: () => void;
}


interface IState {
  // Waiting for the server to start the next list, so the button is not pressed again meanwhile.
  completing: boolean;
}

// Gives the button back if the server never answers.
const COMPLETING_TIMEOUT = 5000;

class NextHandoverPerSchedule extends React.Component<IProps, IState> {
  public state: IState = {completing: false};
  private completingTimer?: number;

  public componentDidUpdate(prevProps: IProps) {
    if (this.state.completing && prevProps.schedule.lastHandover !== this.props.schedule.lastHandover) {
      this.stopCompleting();
    }
  }

  public componentWillUnmount() {
    window.clearTimeout(this.completingTimer);
  }

  public render() {
    const schedule = this.props.schedule;
    const children: string[] = [];
    for (const child of schedule.data.children) {
      children.push(tn.familyMemberName(child, this.props.family));
    }
    const childrenNames = this.formatNames(children);

    const handover = findNextHandover(schedule);
    const nextHandoverTime = handover.time;
    const nextHandoverLocation = handover.location;
    const nextHandoverAtSchool = handover.atSchool;
    const firstEntryParent = handover.dropOffParent;
    const secondEntryParent = handover.pickUpParent;

    let when = "";
    let where = "";
    let title = "";
    let hasBeen = false;

    if (nextHandoverTime === undefined) {
      title = tn.lang.noDataForNextHandover;
      hasBeen = true;
    } else if (handoverHasBeen(handover, Date.now())) {
      title = tn.lang.nextHandoverTitle(childrenNames);
      where = tn.lang.nextHandoverHasBeen;
      hasBeen = true;
      when = "";
    } else {
      const schoolAge = schoolAgeFromFamily(this.props.family);
      title = tn.lang.nextHandoverTitle(childrenNames);
      // Through school the children are handed over during the school day, so there is no time to show.
      when = nextHandoverAtSchool ? tn.lang.handoverDay(new Date(nextHandoverTime))
        : tn.lang.handoverDayAndTime(new Date(nextHandoverTime));
      if (firstEntryParent === this.props.myAccountId) {
        where = tn.lang.handoverDropoff( nextHandoverAtSchool, nextHandoverLocation, schedule.school, schoolAge);
      } else if (secondEntryParent === this.props.myAccountId) {
        where = tn.lang.handoverPickup( nextHandoverAtSchool, nextHandoverLocation, schedule.school, schoolAge);
      } else {
        when = "";
        where = tn.lang.handoverExchange(childrenNames, tn.familyMemberName(secondEntryParent, this.props.family),
          tn.familyMemberName(firstEntryParent, this.props.family), new Date(nextHandoverTime),
          nextHandoverAtSchool, nextHandoverLocation, schedule.school, schoolAge);
      }
    }

    const curatedTodos = packingItemsForSchedule(this.props.todos, handover, this.props.myAccountId);

    // if the parents use different schedules lets warn about this.
    let differentSchedulesWarning:JSX.Element|undefined;
    let parentAActivated:number[]|undefined;
    let parentBActivated:number[]|undefined;
    let parentA:number|undefined;
    let parentB:number|undefined;
    let parentABeenOnline:boolean = false;
    let parentBBeenOnline:boolean = false;
    let hasSynchedActiveSchedules = false;
    let isParent:boolean = false;
    for (const mem of this.props.family) {
      if (mem.id === this.props.myAccountId && mem.role === FamilyMemberType.PARENT) {
        isParent = true;
      }
      if (mem.role === FamilyMemberType.PARENT && mem.calendars) {
        if (parentAActivated === undefined) {
          parentAActivated = mem.calendars;
          parentA = mem.id;
          parentABeenOnline= (mem.lastOnline > 0);
        } else if (parentBActivated === undefined) {
          parentBActivated = mem.calendars;
          parentB = mem.id;
          parentBBeenOnline= (mem.lastOnline > 0);
        }
      }
    }
    if (parentAActivated && parentBActivated) {
      for (const scheduleId of parentAActivated) {
        if (parentBActivated.indexOf(scheduleId) > -1) {
          hasSynchedActiveSchedules = true;
        }
      }
    }

    if (parentAActivated && parentBActivated && !hasSynchedActiveSchedules && parentABeenOnline && parentBBeenOnline) {
      const parentAName = parentA === this.props.myAccountId?tn.lang.youSub:tn.familyMemberName(parentA, this.props.family);
      const parentBName = tn.familyMemberName(parentB, this.props.family);
      differentSchedulesWarning = <div className={this.props.classes.warning} >
        <div style={{flex: 2}}>
          <ReportProblemOutlinedIcon style={{paddingLeft: '8px'}}/>
        </div>
        <div style={{flex: 6}}>
          <div style={{fontSize: '14px', fontWeight: 'bold', lineHeight: '20px'}}>
            {tn.lang.scheduleUsingDifferent}
          </div>
          <div style={{fontWeight: 400, fontSize: '12px',}}>
            {parentAName + tn.lang.scheduleUsingDifferentUses + parentAActivated.map((id:number) =>
              tn.lang.scheduleNameNew(this.props.schedules.find((sch:ISchedule) => sch.id === id)))}
          </div>
          <div style={{fontWeight: 400, fontSize: '12px',}}>
            {parentBName + tn.lang.scheduleUsingDifferentUses + parentBActivated.map((id:number) =>
              tn.lang.scheduleNameNew(this.props.schedules.find((sch:ISchedule) => sch.id === id)))}
          </div>
        </div>
      </div>;
    }

    const classes = this.props.classes;
    const scheduleParents = (firstEntryParent && secondEntryParent) ? [firstEntryParent, secondEntryParent] : [];
    const seenByAll = curatedTodos.length === 0 ? [] : this.props.family.filter((mem: IFamilyMember) =>
      curatedTodos.every((todo: ITodo) => !!todo.seen && todo.seen.indexOf(mem.id) > -1));
    const iHaveSeenAll = seenByAll.some((mem: IFamilyMember) => mem.id === this.props.myAccountId);
    const groups = groupPackingItems(curatedTodos, schedule.data.children);

    return (
      <div className={classes.pane + " " + (this.props.single ? classes.paneSingle : classes.paneStacked)}>
        {differentSchedulesWarning}
        <div className={classes.handoverHeader}>
          <span className={classes.handoverHeaderTitle}>{title}</span>
          {when && <span className={classes.handoverHeaderText}>{when}</span>}
          <span style={{fontSize:'18px'}} className={classes.handoverHeaderText}>{where}</span>
          {hasBeen && (
            <div className={classes.handoverHeaderAction}>
              <Button disabled={!isParent || this.state.completing} variant="contained" color="secondary" size="small"
                onClick={this.completeHandover}>
                {tn.lang.todoNextHandover}
              </Button>
            </div>
          )}
        </div>
        {curatedTodos.length > 0 && !iHaveSeenAll && (
          <div className={classes.seenByAll + " " + classes.unseenNotice}>{tn.lang.packingUnseenNotice}</div>
        )}
        {iHaveSeenAll && (
          <div className={classes.seenByAll}>
            <span>{tn.lang.packingSeenByAll}</span>
            {seenByAll.map((mem: IFamilyMember) =>
              <ImgAvatar key={mem.id} altText={mem.firstName} color={mem.color || '#FFFFFF'} imgName={mem.avatar}
                imgUrl={mem.imageAvatar ? mem.avatarURL : undefined} style={{width: 14, height: 14}} />)}
          </div>
        )}
        <div className={classes.list}>
          {groups.map((group: IPackingGroup) => {
            const groupName = groups.length === 1 ? undefined
              : (group.child === undefined ? tn.lang.packingGeneral : tn.familyMemberName(group.child, this.props.family));
            return (
              <React.Fragment key={group.child === undefined ? 'general' : group.child}>
                {groupName && <div className={classes.groupHeader}>{groupName}</div>}
                {group.items.map((entry: ITodo) =>
                  <PackingListItem
                    key={entry.id}
                    todo={entry}
                    family={this.props.family}
                    myAccountId={this.props.myAccountId}
                    scheduleParents={scheduleParents}
                    scheduleChildren={schedule.data.children}
                    active={!this.props.hide}
                    complete={(complete: boolean) => { this.props.completeTodo({ id: entry.id, complete }); }}
                    seen={() => { this.props.seenTodo({ id: entry.id }); }}
                  />
                )}
                {/* Also after the exchange: new items renew, so they carry over to the next list. */}
                <NewPackingListItemRow scheduleId={this.props.schedule.id} tagged={group.child} groupName={groupName} />
              </React.Fragment>
            );
          })}
        </div>
      </div>
    );
  }

  private completeHandover = () => {
    this.setState({completing: true});
    window.clearTimeout(this.completingTimer);
    this.completingTimer = window.setTimeout(this.stopCompleting, COMPLETING_TIMEOUT);
    this.props.completeHandover();
  };

  private stopCompleting = () => {
    window.clearTimeout(this.completingTimer);
    this.setState({completing: false});
  };

  private formatNames(names: string[]) {
    let nameList = "";
    for (let i: number = 0; i < names.length; i += 1) {
      nameList = nameList + names[i];
      if (names.length > 1) {
        if (i === (names.length - 2)) {
          nameList = nameList + " & ";
        } else if (i < (names.length - 2)) {
          nameList = nameList + ", ";
        }
      }
    }
    return nameList;
  }
}

export default withStyles(styles)(NextHandoverPerSchedule);



// WEBPACK FOOTER //
// ./src/components/next-handover/NextHandoverPerSchedule.tsx